<template>
  <NuxtLink target="_blank" class="hotel-card-compact" :to="getHotelPageAddr(hotel)">
    <div class="hotel-card-compact__image">
      <img
          v-if="!isNoPhoto"
          :alt="hotel.name"
          :src="hotelImageSrcset.src"
          :srcset="hotelImageSrcset.srcset"
          width="320" height="240"
          loading="lazy"
          @error="showPlaceholder"
      />
      <PhotoPlaceholder v-show="isNoPhoto"/>
    </div>
    
    <div class="hotel-card-compact__content">
      <div class="hotel-card-compact__header">
        <div class="hotel-card-compact__title-row">
          <h3 class="hotel-card-compact__title">{{ hotel.name }}</h3>
          <div v-if="hotel.rating" class="hotel-card-compact__stars">
            <img 
              v-for="i in hotel.rating" 
              :key="i"
              src="/images/hotel-card/rating-star.svg"  
              width="12" 
              height="12"  
              alt="star"
            >
          </div>
        </div>
        
        <div v-if="hotel.address" class="hotel-card-compact__address">
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9 16.4248L9.59737 17.0072C9.51898 17.0842 9.42582 17.1452 9.32324 17.1869C9.22065 17.2286 9.11066 17.25 8.99958 17.25C8.8885 17.25 8.77851 17.2286 8.67592 17.1869C8.57334 17.1452 8.48017 17.0842 8.40178 17.0072L8.39672 17.0014L8.38237 16.9874L8.33006 16.9354C8.03263 16.6359 7.74009 16.3317 7.45256 16.023C6.73065 15.2496 6.03939 14.4493 5.38031 13.6239C4.62769 12.6752 3.85819 11.6027 3.27347 10.5434C2.70141 9.50477 2.25 8.37949 2.25 7.3499C2.25 3.60528 5.28075 0.75 9 0.75C12.7192 0.75 15.75 3.60528 15.75 7.3499C15.75 8.37949 15.2986 9.50477 14.7265 10.5426C14.1418 11.6035 13.3732 12.6752 12.6197 13.6239C11.6978 14.7786 10.7131 15.884 9.66994 16.9354L9.61763 16.9874L9.60328 17.0014L9.59822 17.0064L9 16.4248ZM9 9.82487C9.67133 9.82487 10.3152 9.56411 10.7899 9.09997C11.2646 8.63582 11.5312 8.00631 11.5312 7.3499C11.5312 6.6935 11.2646 6.06399 10.7899 5.59984C10.3152 5.13569 9.67133 4.87494 9 4.87494C8.32867 4.87494 7.68484 5.13569 7.21014 5.59984C6.73543 6.06399 6.46875 6.6935 6.46875 7.3499C6.46875 8.00631 6.73543 8.63582 7.21014 9.09997C7.68484 9.56411 8.32867 9.82487 9 9.82487Z" fill="#666"/>
          </svg>
          <span>{{ hotel.address }}</span>
        </div>
      </div>

      <div class="hotel-card-compact__amenities" v-if="topHotelAmenities?.length">
        <div 
          class="hotel-card-compact__amenity" 
          v-for="amenity in topHotelAmenities"
          :key="(amenity as any).code"
        >
          <span class="icon" :class="`icon ${amenity.icon}`"/>
          <span class="amenity-text">{{ amenity.name }}</span>
        </div>
      </div>

      <div class="hotel-card-compact__footer">
        <div class="hotel-card-compact__rating" v-if="hotel.avg_rating && hotel.avg_rating > 0">
          <div :class="['rating-badge', ratingClass]">
            {{ round(hotel.avg_rating) }}
          </div>
          <div class="rating-info">
            <div class="rating-label">
              <span v-if="round(hotel.avg_rating) >= 9">Excellent</span>
              <span v-else-if="round(hotel.avg_rating) >= 7">Very Good</span>
              <span v-else-if="round(hotel.avg_rating) >= 6">Good</span>
              <span v-else-if="round(hotel.avg_rating) >= 4">Fair</span>
              <span v-else>Poor</span>
            </div>
            <div class="rating-reviews">{{ hotel.reviews_count }} reviews</div>
          </div>
        </div>

        <div class="hotel-card-compact__price" v-if="priceTotalDisplay || pricePerNightDisplay">
          <div class="price-label">Total</div>
          <div class="price-value" v-if="priceTotalDisplay">{{ priceTotalDisplay }}</div>
          <div class="price-per-night" v-if="pricePerNightDisplay">{{ pricePerNightDisplay }}/night</div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { TOP_AMENITIES } from "~/utils/hotels";
import { imageUrlWithProxySrcset } from "~/utils/imageproxy";
import { formatPriceDisplay } from "~/utils/currency";

const props = defineProps({
  hotel: { type: Object, required: true },
  route: { type: Object },
  cheapestRate: { type: Object },
  priceFrom: { type: Object }
})

const isNoPhoto = ref(props.hotel.cover_image === null || props.hotel.cover_image?.url.length === 0)
const hotelImageSrcset = computed(() => imageUrlWithProxySrcset(props.hotel.cover_image?.url, 320, 240))

const topHotelAmenities = (() => {
  const _hotelAmenities = props.hotel?.hotel_amenities ? props.hotel.hotel_amenities : []
  return TOP_AMENITIES.filter((amenity: any) => {
    return _hotelAmenities.find((hotelAmenity: any) => Number(hotelAmenity.ota_code) === amenity.code)
  }).slice(0, 3)
})()

const ratingClass = computed(() => {
  const rating = round(props.hotel.avg_rating)
  if (rating >= 9) return 'excellent'
  if (rating >= 7) return 'very-good'
  if (rating >= 6) return 'good'
  if (rating >= 4) return 'fair'
  if (rating > 0) return 'poor'
  return 'no-reviews'
})

const priceTotalDisplay = computed(() => formatPriceDisplay(props.priceFrom))
const pricePerNightDisplay = computed(() => formatPriceDisplay(props.cheapestRate?.night_rate))

function showPlaceholder() {
  isNoPhoto.value = true
}

function round(value: number) {
  return Math.round(value * 10) / 10
}

function getHotelPageAddr(hotel: any) {
  if (!hotel) return ''
  const citySlug = props.route?.params?.city || 'city'
  
  // Include query parameters (dates, pax-mix, etc.) from current route
  const queryFromObject = Object.keys(props.route?.query || {})?.length 
    ? `?${Object.keys(props.route?.query || {}).map(key => `${key}=${(props.route?.query as any)[key]}`).join('&')}` 
    : ''
  
  return `/hotels/${citySlug}/${hotel.id}-${hotel.slug}${queryFromObject}`
}
</script>

<style lang="scss" scoped>
.hotel-card-compact {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  background: var(--white);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s ease;
  cursor: pointer;
  overflow: hidden;
  height: 420px; // Fixed height for consistent card sizes

  &:hover {
    border-color: #1985f9;
    box-shadow: 0 4px 12px rgba(25, 133, 249, 0.1);
    transform: translateY(-2px);
  }

  &__image {
    width: 100%;
    height: 200px;
    overflow: hidden;
    background: #f5f5f5;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    flex: 1; // Fill remaining space after image
    min-height: 0; // Allow flex to shrink if needed
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--black);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__stars {
    display: flex;
    gap: 2px;
    flex-shrink: 0;

    img {
      width: 12px;
      height: 12px;
    }
  }

  &__address {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #666;
    line-height: 1.4;

    svg {
      flex-shrink: 0;
      width: 14px;
      height: 14px;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__amenities {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    max-height: 48px; // Limit to ~2 rows of amenities
    overflow: hidden;
  }

  &__amenity {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #666;

    .icon {
      font-size: 16px;
      color: #999;
    }

    .amenity-text {
      white-space: nowrap;
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 60px; // Ensure consistent footer height
  }

  &__rating {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto; // Don't grow or shrink

    .rating-badge {
      padding: 6px 10px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 700;
      color: white;

      &.excellent {
        background: #047857;
      }

      &.very-good {
        background: #16a34a;
      }

      &.good {
        background: #65a30d;
      }

      &.fair {
        background: #f59e0b;
      }

      &.poor {
        background: #dc2626;
      }
    }

    .rating-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .rating-label {
      font-size: 13px;
      font-weight: 600;
      color: var(--black);
    }

    .rating-reviews {
      font-size: 12px;
      color: #666;
    }
  }

  &__price {
    text-align: right;
    margin-left: auto; // Always push to right even without rating
    flex-shrink: 0; // Don't allow shrinking

    .price-label {
      font-size: 11px;
      color: #666;
      margin-bottom: 2px;
    }

    .price-value {
      font-size: 20px;
      font-weight: 700;
      color: var(--black);
      line-height: 1.2;
    }

    .price-per-night {
      font-size: 13px;
      color: #666;
      margin-top: 2px;
    }
  }
}
</style>
